import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Send, MessageCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";
import { toast } from "sonner";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }
});

export default function ReviewPage() {
  const [reviews, setReviews] = useState([]);
  const [myReview, setMyReview] = useState(null);
  const [reviewText, setReviewText] = useState("");
  const [reviewName, setReviewName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();
  const canWrite = user && user.has_paid;

  useEffect(() => {
    API.get("/reviews").then(res => {
      setReviews(res.data.reviews || []);
      if (user) {
        const mine = res.data.reviews?.find(r => r.user_id === user.email);
        if (mine) {
          setMyReview(mine);
          setReviewText(mine.text);
          setReviewName(mine.display_name);
        }
      }
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [user]);

  const handleSubmit = async () => {
    if (!reviewText.trim()) return;
    setSubmitting(true);
    try {
      await API.post("/reviews", {
        text: reviewText.trim(),
        display_name: reviewName.trim() || "Anonym"
      });
      const res = await API.get("/reviews");
      setReviews(res.data.reviews || []);
      const mine = res.data.reviews?.find(r => r.user_id === user?.email);
      setMyReview(mine);
      toast.success("Tack! Din recension granskas innan den publiceras.");
    } catch {
      toast.error("Kunde inte spara");
    }
    setSubmitting(false);
  };

  const displayReviews = canWrite
    ? reviews.filter(r => r.user_id !== user?.email)
    : reviews;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-[#A0655A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="reviews-page">
      <Navigation />

      <div className="pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 sm:px-6">
        <div className="max-w-2xl mx-auto">

          <motion.div {...fadeUp()} className="text-center mb-10 sm:mb-14">
            <p className="text-xs sm:text-sm uppercase tracking-[0.25em] text-[#A0655A] font-medium mb-4">Recensioner</p>
            <h1 className="heading-font text-3xl sm:text-4xl lg:text-5xl font-medium text-[#111111] mb-3 leading-tight" data-testid="reviews-page-title">
              Röster från resan
            </h1>
            <p className="text-[#111111]/50 text-sm sm:text-base max-w-md mx-auto leading-relaxed">
              {canWrite
                ? "Läs vad andra som gjort metoden tycker — och dela gärna din egen upplevelse."
                : "Så här beskriver andra sin upplevelse av metoden."}
            </p>
          </motion.div>

          {/* Write / edit review form — only for paid users */}
          {canWrite && (
            <motion.div {...fadeUp(0.1)} className="mb-10 sm:mb-14">
              <div className="bg-white border border-[#EBE3DF] rounded-2xl p-5 sm:p-6" data-testid="review-form-card">
                <div className="flex items-center gap-2 mb-4">
                  <MessageCircle className="text-[#A0655A]" size={18} />
                  <p className="text-sm font-medium text-[#111111]">
                    {myReview ? 'Din recension' : 'Dela din upplevelse'}
                  </p>
                </div>

                <Textarea
                  placeholder="Berätta hur du upplever metoden..."
                  value={reviewText}
                  onChange={(e) => setReviewText(e.target.value)}
                  rows={4}
                  className="rounded-xl mb-3 border-[#EBE3DF] focus:border-[#A0655A] resize-none text-sm leading-relaxed"
                  data-testid="review-text-input"
                />

                <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
                  <input
                    type="text"
                    placeholder="Ditt namn (eller lämna tomt för 'Anonym')"
                    value={reviewName}
                    onChange={(e) => setReviewName(e.target.value)}
                    className="flex-1 px-3 py-2.5 rounded-xl border border-[#EBE3DF] bg-white text-sm focus:outline-none focus:border-[#A0655A] text-[#111111]"
                    data-testid="review-name-input"
                  />
                  <Button
                    onClick={handleSubmit}
                    disabled={!reviewText.trim() || submitting}
                    className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-6"
                    data-testid="submit-review-btn"
                  >
                    <Send size={14} className="mr-2" />
                    {submitting ? 'Sparar...' : myReview ? 'Uppdatera' : 'Skicka'}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}

          {/* All reviews */}
          {displayReviews.length > 0 ? (
            <div className="space-y-4">
              {displayReviews.map((review, i) => (
                <motion.div
                  key={i}
                  {...fadeUp(0.05 + i * 0.04)}
                  className="bg-[#FDF9F7] border border-[#EBE3DF]/40 rounded-2xl p-5 sm:p-6"
                  data-testid={`review-card-${i}`}
                >
                  <p className="text-sm text-[#111111]/65 leading-relaxed mb-3 italic">
                    "{review.text}"
                  </p>
                  <p className="text-xs text-[#A0655A] font-medium">— {review.display_name}</p>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-[#111111]/35 text-sm">Inga recensioner ännu.</p>
            </div>
          )}

          {/* CTA for non-logged-in visitors */}
          {!user?.has_paid && (
            <motion.div {...fadeUp(0.2)} className="text-center mt-14 pt-8 border-t border-[#EBE3DF]/50">
              <p className="text-[#111111]/50 text-sm mb-5">Vill du börja din egen resa?</p>
              <Button onClick={() => navigate(user ? '/betalning' : '/registrera')}
                className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-8 py-5 text-base"
                data-testid="reviews-cta-btn">
                {user ? 'Gå till betalning' : 'Börja din resa'}
                <ArrowRight className="ml-2" size={16} />
              </Button>
            </motion.div>
          )}

        </div>
      </div>

      <Footer />
    </div>
  );
}
